import {LayoutContext} from 'apstra-ui-common';
import {Icon, List} from 'semantic-ui-react';
import {find, isEmpty, map, reverse, sortBy, throttle, transform} from 'lodash';
import {Link, useLocation} from 'react-router-dom';
import React, {memo, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import cx from 'classnames';

import {EllipsisPopup} from '../../../components/EllipsisPopup';
import {REPORT_TYPES} from './const';

import './ReportToC.less';

const ReportToC = ({
  items, sectionRefs, resetScrolledRef, delay, tocContainerPadding,
  containerTop, reportHeight, collapsedSectionState, onCollapseToggle,
}) => {
  const containerRef = useRef();
  const {mainContentElement} = useContext(LayoutContext);

  const parentTop = useMemo(() => {
    if (containerRef.current) {
      const {top} = containerRef.current.getBoundingClientRect();
      return top;
    }
    return containerTop;
  }, [containerRef, containerTop]);

  // we need to follow report height to recalculate sections' positions
  // after finishing expand/collapse process
  const sectionsScrollY = useMemo(() => {
    const castFlatPathList = (items) => {
      return transform(items, (result, {path, items}) => {
        result.push(path);
        result.push(...castFlatPathList(items));
      });
    };
    return reverse(sortBy(transform(castFlatPathList(items), (result, path) => {
      const element = sectionRefs[path]?.current;
      if (element) {
        const {y} = element.getBoundingClientRect();
        result.push({path, y: y + mainContentElement.scrollTop});
      }
    }, []), 'y'));
  }, [items, sectionRefs, reportHeight]); // eslint-disable-line react-hooks/exhaustive-deps

  const [selectedSection, setSelectedSection] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onScroll = useCallback(throttle(() => {
    const currentSection = find(
      sectionsScrollY,
      ({y}) => y < mainContentElement.scrollTop + parentTop + tocContainerPadding
    );
    if (currentSection?.path !== selectedSection) {
      setSelectedSection(currentSection?.path);
    }
  }, delay),
  [mainContentElement, parentTop, sectionsScrollY, selectedSection, tocContainerPadding]);

  useEffect(() => {
    mainContentElement.addEventListener('scroll', onScroll);
    return () => {
      mainContentElement.removeEventListener('scroll', onScroll);
    };
  }, [mainContentElement, onScroll]);

  const [hoveredPath, setHovered] = useState('');

  return (
    <div
      ref={containerRef}
      className={cx('report-toc')}
    >
      <List onMouseLeave={() => setHovered('')}>
        <ToC
          items={items}
          selectedSection={selectedSection}
          resetScrolledRef={resetScrolledRef}
          containerRef={containerRef}
          setHovered={setHovered}
          hoveredPath={hoveredPath}
          collapsedSectionState={collapsedSectionState}
          onCollapseToggle={onCollapseToggle}
        />
      </List>
    </div>
  );
};

ReportToC.defaultProps = {
  delay: 30,
  tocContainerPadding: 5,
};

const ToC = ({
  items, resetScrolledRef, selectedSection, containerRef, hoveredPath, setHovered,
  collapsedSectionState, onCollapseToggle,
}) => {
  return map(items, ({title, path, items}) => (
    <List.Item
      key={path}
      className={cx({
        selected: selectedSection === path,
        hovered: hoveredPath === path,
      })}
    >
      <ItemToC
        title={title}
        path={path}
        resetScrolledRef={resetScrolledRef}
        onHover={setHovered}
        collapsed={collapsedSectionState[path]}
        onCollapseToggle={onCollapseToggle}
        selected={selectedSection === path}
      />
      {!isEmpty(items) && (
        <List.List>
          <ToC
            items={items}
            selectedSection={selectedSection}
            resetScrolledRef={resetScrolledRef}
            containerRef={containerRef}
            hoveredPath={hoveredPath}
            setHovered={setHovered}
            collapsedSectionState={collapsedSectionState}
            onCollapseToggle={onCollapseToggle}
          />
        </List.List>
      )}
    </List.Item>
  ));
};

const ItemToC = memo(({title, path, type, resetScrolledRef, onHover, collapsed, onCollapseToggle, selected}) => {
  const ref = useRef();
  const {pathname, search} = useLocation();
  const caretDirection = collapsed ? 'right' : 'down';
  useEffect(() => {
    if (!selected) return;
    ref?.current.scrollIntoView({block: 'nearest'});
  }, [selected]);
  return (
    <Link
      ref={ref}
      className={cx('toc-item', {'toc-chapter': path.length === 1})}
      to={`${pathname}${search}#header.${path}`}
      onClick={resetScrolledRef}
      onMouseEnter={() => onHover(path)}
    >
      <Icon
        link
        name={type === REPORT_TYPES.section ? 'circle' : `caret ${caretDirection}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onCollapseToggle(path);
        }}
      />
      <EllipsisPopup
        content={title}
        trigger={
          <span>{title}</span>
        }
      />
    </Link>
  );
});

export default memo(ReportToC);
